import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    sortFood,
    setFoodAmount,
    setFoodAsDrink,
    addFoodModification,
    removeFoodModification,
    removeFood,
    moveFoodToPlate,
    openModal,
    closeModal
} from 'actions/index';
import Util from 'utility';
import { Scrollbars } from 'react-custom-scrollbars';
import UnitConvert from 'UnitConverter';
import shortid from 'shortid';
import api from 'api';
import { __ } from 'locale';

const itemInfoCls = 'flex items-center text-[0.85em] mt-[4px]';
const itemInfoIconCls = 'w-[2em] text-center text-[#26AA52] cursor-pointer shrink-0';
const itemWeightInputCls = 'flex items-center ml-[0.3em] [&>input]:w-[4em] [&>input]:border [&>input]:border-[#ccc] [&>input]:py-[2px] [&>input]:px-[4px] [&>input]:text-center [&>span]:ml-[2px] [&>span]:text-[#888]';
const modOptionCls = 'inline-flex [&>input]:hidden [&>label]:inline-flex [&>label]:items-center [&>label]:cursor-pointer [&>label]:py-[3px] [&>label]:px-[6px] [&>label]:rounded [&>label]:border [&>label]:border-[#ccc] [&>label]:m-[2px] [&>input:checked+label]:bg-[#26AA52] [&>input:checked+label]:text-white [&>input:checked+label]:border-[#26AA52]';

function FoodRow({ food, action, readOnly }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const { globals } = data;

    const [weightStr, setWeightStr] = useState(() => food.weight.toString());
    const [isFocused, setIsFocused] = useState(false);
    const [showModificationsPanel, setShowModificationsPanel] = useState(false);
    const [selectedModificationHelpItem, setSelectedModificationHelpItem] = useState(false);
    const [modificationHelpModalIsOpen, setModificationHelpModalIsOpen] = useState(false);
    const [moveToPlateFood, setMoveToPlateFood] = useState(null);
    const [moveFoodToPlateTargetId, setMoveFoodToPlateTargetId] = useState('');
    const [moveFoodToPlateDialogOpen, setMoveFoodToPlateDialogOpen] = useState(false);
    const [uniqid] = useState(() => shortid());

    const weightInputRef = useRef(null);

    const item = globals.itemData[food.food_id];

    const focusWeightInput = () => {
        if (weightInputRef.current) {
            weightInputRef.current.focus();
            weightInputRef.current.select();
        }
    };

    const handleWeightChange = (e) => {
        setWeightStr(e.target.value);
    };

    const handleWeightInputFocus = () => {
        setIsFocused(true);
    };

    const handleWeightInputBlur = (e) => {
        let weight = e.target.value.replace(',', '.') * 1;
        if (weight < item.weight_min || weight > item.weight_max) {
            alert('Neplatné množství potraviny - zadejte prosím množství od ' + item.weight_min + ' do ' + item.weight_max + ' ' + item.weight_unit);
            weight = item.weight;
        }
        setWeightStr(String(weight));
        setIsFocused(false);
        dispatch(setFoodAmount({ mealHash: data.activeMealHash, foodId: food.id, weight }));
        Util.doLoadEvaluation();
    };

    const handleDrinkChange = (e) => {
        dispatch(setFoodAsDrink({
            mealHash: data.activeMealHash,
            foodId: food.id,
            is_drink: e.target.checked ? 1 : 0
        }));
        Util.doLoadEvaluation();
    };

    const loadFoodDescription = (foodId) => {
        if (globals.accessType === 'demo') {
            alert('Zobrazovat popisy potravin je možná pouze v plnohodnotné verzi aplikace ZOF.');
            return;
        }
        api('food/description/' + foodId).then(result => {
            result = result.data;
            if (result.error) return false;
            dispatch(closeModal());
            dispatch(openModal({
                title: result.title,
                class: 'dialog dialog-flexible',
                content: (
                    <div className="p-[0.5em]">
                        <div dangerouslySetInnerHTML={{ __html: result.description }} />
                    </div>
                )
            }));
        });
    };

    const showModificationHelp = (prepId) => {
        api('modification-help/' + prepId).then(result => {
            result = result.data;
            if (result.error) return false;
            setSelectedModificationHelpItem(result);
            setModificationHelpModalIsOpen(true);
        });
    };

    const removeFoodFromRecipe = (id) => {
        if (globals.accessType === 'demo') {
            alert('Mazat potarviny z receptu je možná pouze v plnohodnotné verzi aplikace ZOF.');
            return;
        }
        dispatch(removeFood(id));
        Util.doLoadEvaluation();
    };

    const sortFoodInRecipe = (foodId, direction) => {
        dispatch(sortFood({ foodId, direction }));
    };

    const modificationToggle = (e) => {
        if (e.target.checked) {
            dispatch(addFoodModification({ foodId: food.id, modificationId: e.target.value }));
        } else {
            dispatch(removeFoodModification({ foodId: food.id, modificationId: e.target.value }));
        }
        Util.doLoadEvaluation();
    };

    const doMoveFoodToPlate = () => {
        if (moveFoodToPlateTargetId === '') {
            alert('Vyberte cílový talíř');
            return;
        }
        dispatch(moveFoodToPlate({ foodId: moveToPlateFood.id, foodGroupId: moveFoodToPlateTargetId }));
        setMoveFoodToPlateDialogOpen(false);
    };

    // Modifications list
    let modificationsNumber = 0;
    let modificationsList = [];
    if (typeof data.modifications[food.id] !== 'undefined') {
        data.modifications[food.id].forEach(key => {
            modificationsNumber++;
            modificationsList.push(globals.preparation_types[key]);
        });
    }
    const modificationsNumberEl = modificationsNumber > 0 ? <span>{modificationsNumber}</span> : false;
    const modListStr = modificationsList.length > 0 ? modificationsList.join(', ') : 'Bez úprav';

    // Modification help modal
    const modificationHelpModal = modificationHelpModalIsOpen ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]" onClick={() => setModificationHelpModalIsOpen(false)}>
            <div className="bg-white rounded shadow-lg max-w-[460px] w-full overflow-hidden">
                <h2>{selectedModificationHelpItem.title} <a><i onClick={() => setModificationHelpModalIsOpen(false)} className="fa fa-times"></i></a></h2>
                <div className="p-[1rem]">
                    <div style={{ height: '250px' }}>
                        <Scrollbars heightrelativetoparent="1">
                            <div style={{ flex: '1', overflow: 'hidden', padding: '0 14px 0 0' }}>
                                <div dangerouslySetInnerHTML={{ __html: selectedModificationHelpItem.description }}></div>
                                <p style={{ paddingTop: '1em', marginTop: '1em', borderTop: '1px solid #ddd' }}>Více se o nutriční kvalitě výživy dozvíte na <a style={{ color: '#4eb32d' }} href="https://www.kurzyatac.cz">www.kurzyatac.cz</a></p>
                            </div>
                        </Scrollbars>
                    </div>
                </div>
            </div>
        </div>
    ) : false;

    // Normalized unit info
    const foodItem = {
        id: food.food_id,
        selectedWeight: food.weight,
        weight: globals.itemData[food.food_id].weight,
        norm_unit_amount: globals.itemData[food.food_id].norm_unit_amount * 1.0,
        norm_unit_unit: globals.itemData[food.food_id].norm_unit_unit,
        norm_unit2_amount: globals.itemData[food.food_id].norm_unit2_amount * 1.0,
        norm_unit2_unit: globals.itemData[food.food_id].norm_unit2_unit
    };
    const uc = UnitConvert;
    const normalizedUnitInfoRaw = uc.convert(foodItem);
    const normalizedUnitInfo = (normalizedUnitInfoRaw !== null && normalizedUnitInfoRaw !== '' && normalizedUnitInfoRaw !== ' null')
        ? <span className="text-[0.85em] text-[#888]">{normalizedUnitInfoRaw}</span>
        : false;

    const normalizedUnitInfoElement = normalizedUnitInfo
        ? <span className="ml-[0.5em] text-[0.85em]">{normalizedUnitInfo}</span>
        : false;

    // Weight range info (shown when focused)
    const weightRangeInfo = isFocused ? (
        <div className="text-[0.8em] text-[#888] ml-[0.5em]">
            <p>{normalizedUnitInfo}</p>
            <p>Rozpětí: {item.weight_min} - {item.weight_max} {item.weight_unit}</p>
        </div>
    ) : false;

    // Undesirable foods
    const mealProfiles = Object.keys(globals.profiles).map(Number);
    let undesirableFoodForProfiles = [];
    for (let profileId in globals.undesirableFoods) {
        profileId = profileId * 1;
        if (!mealProfiles.includes(profileId)) continue;
        if (globals.undesirableFoods[profileId].includes(food.food_id)) {
            undesirableFoodForProfiles.push(profileId);
        }
    }

    if (typeof data.appModeData !== 'undefined') {
        undesirableFoodForProfiles = [];
        if (typeof data.appModeData.undesirableFoods !== 'undefined') {
            if (data.appModeData.undesirableFoods.includes(food.food_id)) {
                if (typeof data.appModeData.profileId !== 'undefined') {
                    undesirableFoodForProfiles.push(data.appModeData.profileId * 1);
                }
            }
        }
    }

    let itemRowClass = undesirableFoodForProfiles.length > 0
        ? 'flex relative items-center mb-[0.5em] last:mb-0 bg-[#fff5f5] shadow-[inset_0_8px_16px_rgba(0,0,0,0.025)]'
        : 'flex relative items-center mb-[0.5em] last:mb-0 bg-white shadow-[inset_0_8px_16px_rgba(0,0,0,0.025)]';

    let undesirableFoodWarning = undesirableFoodForProfiles.map(profileId => {
        const profile = globals.profiles[profileId];
        const profileColor = profile.profile_color || '999999';
        return <span key={profile.name} style={{ backgroundColor: '#' + profileColor, color: '#fff' }} className="text-[0.75em] font-bold uppercase px-[4px] py-[2px] rounded mr-[2px]">{profile.name}</span>;
    });

    let undesirableFoodWarningEl = false;
    if (undesirableFoodWarning.length > 0) {
        undesirableFoodWarningEl = (
            <div className="flex text-[0.85em] mt-[2px] text-[#f00] [&_span]:text-[0.85em] [&_span]:mr-[0.5em] [&_span]:font-bold [&_span]:uppercase">
                <div className=""><i className="fa fa-exclamation-triangle"></i></div>
                <div className="inline-flex flex-wrap ml-[0.5em]">
                    <span>Nevhodné pro:</span>{undesirableFoodWarning}
                </div>
            </div>
        );
    }

    // Allergen warning (only shown when food is also undesirable)
    let alergenWarning = false;
    if (typeof globals.foodAlergens[food.food_id] === 'object' && undesirableFoodWarningEl) {
        const alergens = globals.foodAlergens[food.food_id].map(id => globals.itemAlergens[id]);
        if (alergens.length > 0) {
            alergenWarning = <p style={{ color: '#f00', fontWeight: 'bold', fontSize: '.8em', margin: '7px 0 0' }}>{__('Alergeny')}: {alergens.join(', ')}</p>;
        }
    }

    // Drink mode selection
    let drinkModeSelection = false;
    if (item.is_drink == 1) {
        const cbName = 'is_drink_' + food.food_id;
        drinkModeSelection = (
            <label><input type="checkbox" name={cbName} onChange={handleDrinkChange} checked={food.is_drink == 1} /> pitný režim</label>
        );
    }

    // Modifications panel
    let modificationsPanel = <div className="ml-[0.5em] text-[0.85em] text-[#888]">{modListStr}</div>;
    if (showModificationsPanel) {
        const disableUnchecked = typeof data.modifications[food.id] !== 'undefined' && data.modifications[food.id].length >= 6;
        const modItems = [];
        const preparationIds = Object.values(globals.preparation_type_ids_sorted)
            .filter(prepId => prepId != 0)
            .sort((a, b) => globals.preparation_types[a].localeCompare(globals.preparation_types[b], 'cs', { sensitivity: 'base' }));

        preparationIds.forEach(prepId => {
            const checked = typeof data.modifications[food.id] !== 'undefined' && data.modifications[food.id].indexOf(prepId) > -1;
            const icon = <img className="w-[28px] h-[28px] shrink-0 mr-[4px]" src={`https://www.zofapp.cz/data/zof/modifications/${prepId}.svg`} alt="" />;
            const cbName = `cb_${uniqid}_[]`;
            const prepUniqId = `prep_${uniqid}_${prepId}`;

            if (disableUnchecked && !checked) {
                modItems.push(
                    <div className={modOptionCls} key={prepId}>
                        <input type="checkbox" name={cbName} id={prepUniqId} value={prepId} checked={checked} onChange={modificationToggle} />
                        <label htmlFor={prepUniqId} style={{ opacity: 0.4 }}>
                            {icon}<span>{globals.preparation_types[prepId]}</span>
                        </label>
                    </div>
                );
            } else {
                modItems.push(
                    <div className={modOptionCls} key={prepId}>
                        <input type="checkbox" name={cbName} id={prepUniqId} value={prepId} checked={checked} onChange={modificationToggle} />
                        <label htmlFor={prepUniqId}>
                            {icon}<span>{globals.preparation_types[prepId]}</span>
                        </label>
                        {!disableUnchecked && <a className="ml-[4px] text-[0.75em] text-[#999] cursor-pointer hover:text-[#26AA52]" onClick={() => showModificationHelp(prepId)}><i className="fa fa-question-circle"></i></a>}
                    </div>
                );
            }
        });
        modificationsPanel = <div className="flex flex-wrap mt-[0.5em]">{modItems}</div>;
    }

    // Amount change and modifications (gastro / read-only / normal modes)
    let amountChange, modifications;

    if (readOnly === true) {
        amountChange = (
            <div className={itemInfoCls}>
                <div className={itemInfoIconCls}><i className="fa fa-balance-scale"></i></div>
                <div className={itemWeightInputCls}>
                    <span style={{ display: 'inline-block', padding: '4px 2px', border: 'none' }}>
                        {weightStr}{'\u00A0'}{item.weight_unit}
                    </span>
                </div>
                {normalizedUnitInfoElement}
            </div>
        );
        modifications = (
            <div className={itemInfoCls}>
                <div className="ml-[0.5em] text-[0.85em] text-[#888]">{modListStr}</div>
            </div>
        );
        if (food.is_drink == 1) {
            drinkModeSelection = <span style={{ margin: '0 0 0 1em', fontSize: '.75em', color: '#555' }}><i className="fa fa-check"></i>{'\u00A0'}pitný režim</span>;
        } else {
            drinkModeSelection = false;
        }
    } else if (data.plannerEditMode === 'GASTRO_DAILY_MENU') {
        amountChange = (
            <div className={itemInfoCls}>
                <div className={itemInfoIconCls}><i className="fa fa-balance-scale"></i></div>
                <div className={itemWeightInputCls}>
                    <input type="text" readOnly={true} name="weight" value={weightStr} style={{ border: 'none' }} />
                    <span style={{ border: 'none' }}>{item.weight_unit}</span>
                </div>
                {normalizedUnitInfoElement}
            </div>
        );
        modifications = (
            <div className={itemInfoCls}>
                <div className="ml-[0.5em] text-[0.85em] text-[#888]">{modListStr}</div>
            </div>
        );
    } else {
        amountChange = (
            <div className={itemInfoCls} onClick={focusWeightInput}>
                <div className={itemInfoIconCls}><i className="fa fa-balance-scale"></i></div>
                <div className={itemWeightInputCls}>
                    <input
                        ref={weightInputRef}
                        type="text" name="weight"
                        onFocus={handleWeightInputFocus}
                        onBlur={handleWeightInputBlur}
                        onChange={handleWeightChange}
                        value={weightStr}
                    />
                    <span>{item.weight_unit}</span>
                </div>
                {normalizedUnitInfoElement}
            </div>
        );
        modifications = (
            <div className={itemInfoCls}>
                <div className={`${itemInfoIconCls} hover:text-[#1a8a40]`} data-food-id={food.id} onClick={() => setShowModificationsPanel(v => !v)}>
                    <i className="fa fa-tasks"></i>
                </div>
                {modificationsPanel}
            </div>
        );
    }

    // Row options (sort, remove, move)
    const foodRowOptions = (!Util.isReadOnly() && readOnly !== true) ? (
        <div className="ml-auto shrink-0 grow-0 basis-5 flex flex-col [&>a]:text-[#bbb] [&>a]:no-underline [&>a]:text-center [&>a]:inline-block [&>a]:w-[2em] [&>a]:h-[2em] [&>a]:leading-[2em] [&>a]:text-[9px] [&>a:hover]:text-[#222] [&>a.item-remove:hover]:text-[#f00]">
            <a className="item-move-to-plate" onClick={() => { setMoveToPlateFood(food); setMoveFoodToPlateDialogOpen(true); }}>
                <i className="fa fa-bars"></i>
            </a>
            <a className="item-description" onClick={() => loadFoodDescription(food.food_id)}>
                <i className="fa fa-question"></i>
            </a>
            <a className="item-sort" onClick={() => sortFoodInRecipe(food.id, -1)}>
                <i className="fa fa-arrow-up"></i>
            </a>
            <a className="item-sort" onClick={() => sortFoodInRecipe(food.id, 1)}>
                <i className="fa fa-arrow-down"></i>
            </a>
            <a className="item-remove" onClick={() => removeFoodFromRecipe(food.id)}>
                <i className="fa fa-times"></i>
            </a>
        </div>
    ) : false;

    // Move to plate dialog
    let moveFoodToPlateDialog = false;
    if (moveFoodToPlateDialogOpen && moveToPlateFood) {
        const foodGroupsOptions = [<option key="" value="">Vyberte talíř...</option>];
        for (let g in data.foodGroups[data.activeMealHash]) {
            const group = data.foodGroups[data.activeMealHash][g];
            const typeNames = { plate: 'Nepojmenovaný talíř', bowl: 'Nepojmenovaná miska', cup: 'Nepojmenovaný šálek', glass: 'Nepojmenovaná sklenice' };
            const fgTitle = group.title || typeNames[group.type] || '';
            foodGroupsOptions.push(<option key={g} value={g}>{fgTitle}</option>);
        }
        moveFoodToPlateDialog = (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
                <div className="bg-white rounded shadow-lg max-w-[460px] w-full overflow-hidden">
                    <h2>Přesunout potravinu „{moveToPlateFood.title}" <a><i onClick={() => setMoveFoodToPlateDialogOpen(false)} className="fa fa-times"></i></a></h2>
                    <div className="p-[1rem]">
                        <table><tbody>
                            <tr><td>Přesunout do:</td><td><select onChange={e => setMoveFoodToPlateTargetId(e.target.value)}>{foodGroupsOptions}</select></td></tr>
                            <tr><td></td><td><input type="button" onClick={doMoveFoodToPlate} value="Přesunout" /></td></tr>
                        </tbody></table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={itemRowClass} key={food.id}>
            <div className="shrink-0 grow-0 basis-[80px] mr-[0.25em]">
                <img className="h-[85px] w-auto block relative bg-white rounded-[3px] p-[10px] transition-all duration-[150ms]" src={food.image} alt="" />
            </div>
            <div className="shrink grow basis-[15em] py-[0.5em] pr-[1em] pl-[0.25em]">
                <h4>
                    <strong>{food.title}</strong>
                    {drinkModeSelection}
                </h4>
                {amountChange}
                {modifications}
                {alergenWarning}
                {undesirableFoodWarningEl}
            </div>
            {foodRowOptions}
            {moveFoodToPlateDialog}
            {modificationHelpModal}
        </div>
    );
}

export default FoodRow;
